import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Save,
  X,
  Plus,
  Trash2,
  Calendar,
  Banknote,
  Users,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { projectService } from "@/services/projectService";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { toast } from "react-hot-toast";
// BudgetSelector removed - now using text input

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    status: "planning",
    priority: "medium",
    start_date: "",
    end_date: "",
    budget_name: "", // Changed: Budget as text input instead of selection
    budget_allocated: "",
    currency: "NGN",
    manager_id: "",
    department_id: "",
    client_name: "",
    client_contact: "",
    client_email: "",
    is_billable: true,
    milestones: [],
    risks: [],
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [expandedMilestones, setExpandedMilestones] = useState({});

  // Fetch project data if editing
  const { data: project, isLoading: projectLoading } = useQuery(
    ["project", id],
    () => projectService.getProjectById(id),
    {
      enabled: isEditing,
      select: (response) => response?.data?.data?.project,
      onSuccess: (data) => {
        if (data) {
          let milestones = [];
          if (data.milestones) {
            try {
              const parsed =
                typeof data.milestones === "string"
                  ? JSON.parse(data.milestones)
                  : data.milestones;
              milestones = Array.isArray(parsed) ? parsed : [];
            } catch {
              milestones = [];
            }
          }

          let risks = [];
          if (data.risks) {
            try {
              const parsed =
                typeof data.risks === "string"
                  ? JSON.parse(data.risks)
                  : data.risks;
              risks = Array.isArray(parsed) ? parsed : [];
            } catch {
              risks = [];
            }
          }

          setFormData({
            name: data.name || "",
            code: data.code || "",
            description: data.description || "",
            status: data.status || "planning",
            priority: data.priority || "medium",
            start_date: data.start_date?.split("T")[0] || "",
            end_date: data.end_date?.split("T")[0] || "",
            budget_name: data.budget_name || "", // Changed: Load budget_name
            budget_allocated: data.budget_allocated || "",
            currency: data.currency || "NGN",
            manager_id: data.manager_id || "",
            department_id: data.department_id || "",
            client_name: data.client_name || "",
            client_contact: data.client_contact || "",
            client_email: data.client_email || "",
            is_billable: data.is_billable ?? true,
            milestones,
            risks,
            notes: data.notes || "",
          });
        }
      },
    },
  );

  const { data: employees } = useQuery(
    "employees",
    () => projectService.getEmployees(),
    {
      select: (r) => r?.data?.data?.employees || r?.data?.employees || [],
    },
  );

  const { data: departments } = useQuery(
    "departments",
    () => projectService.getDepartments(),
    {
      select: (r) => r?.data?.data?.departments || r?.data?.departments || [],
    },
  );

  const mutation = useMutation(
    (data) =>
      isEditing
        ? projectService.updateProject(id, data)
        : projectService.createProject(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("projects");
        toast.success(
          `Project ${isEditing ? "updated" : "created"} successfully`,
        );
        navigate("/projects");
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || "Failed to save project";
        toast.error(errorMessage);

        // Handle budget-specific errors
        if (errorMessage.toLowerCase().includes("budget")) {
          setErrors((prev) => ({ ...prev, budget_name: errorMessage }));
        }
      },
    },
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Milestone functions
  const addMilestone = () => {
    const newMilestone = {
      id: Date.now(),
      name: "",
      start_date: "",
      end_date: "",
      status: "pending",
      budget: "",
      deliverables: [],
    };
    setFormData((prev) => ({
      ...prev,
      milestones: [...prev.milestones, newMilestone],
    }));
    setExpandedMilestones((prev) => ({ ...prev, [newMilestone.id]: true }));
  };

  const updateMilestone = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, i) =>
        i === index ? { ...m, [field]: value } : m,
      ),
    }));
  };

  const removeMilestone = (index) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index),
    }));
  };

  // Deliverable functions
  const addDeliverable = (milestoneIndex) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => {
        if (i === milestoneIndex) {
          return {
            ...m,
            deliverables: [
              ...(m.deliverables || []),
              { name: "", start_date: "", end_date: "", budget: "", status: "pending" },
            ],
          };
        }
        return m;
      }),
    }));
  };

  const updateDeliverable = (
    milestoneIndex,
    deliverableIndex,
    field,
    value,
  ) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, mi) => {
        if (mi === milestoneIndex) {
          return {
            ...m,
            deliverables: m.deliverables.map((d, di) =>
              di === deliverableIndex ? { ...d, [field]: value } : d,
            ),
          };
        }
        return m;
      }),
    }));
  };

  const removeDeliverable = (milestoneIndex, deliverableIndex) => {
    setFormData((prev) => ({
      ...prev,
      milestones: prev.milestones.map((m, mi) => {
        if (mi === milestoneIndex) {
          return {
            ...m,
            deliverables: m.deliverables.filter(
              (_, di) => di !== deliverableIndex,
            ),
          };
        }
        return m;
      }),
    }));
  };

  // Validate deliverable dates within milestone
  const validateDeliverableDates = (milestone, deliverable) => {
    if (!milestone.start_date || !milestone.end_date) return true;
    if (!deliverable.start_date || !deliverable.end_date) return true;
    return (
      deliverable.start_date >= milestone.start_date &&
      deliverable.end_date <= milestone.end_date
    );
  };

  // Risk functions
  const addRisk = () => {
    setFormData((prev) => ({
      ...prev,
      risks: [
        ...prev.risks,
        {
          description: "",
          impact: "medium",
          probability: "medium",
          mitigation: "",
          status: "open",
        },
      ],
    }));
  };

  const updateRisk = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      risks: prev.risks.map((r, i) =>
        i === index ? { ...r, [field]: value } : r,
      ),
    }));
  };

  const removeRisk = (index) => {
    setFormData((prev) => ({
      ...prev,
      risks: prev.risks.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Project name is required";
    if (!formData.code.trim()) newErrors.code = "Project code is required";
    if (!formData.start_date) newErrors.start_date = "Start date is required";
    if (!formData.end_date) newErrors.end_date = "End date is required";
    if (!formData.manager_id)
      newErrors.manager_id = "Project manager is required";
    // Budget name validation removed - will be auto-generated
    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_date >= formData.end_date
    ) {
      newErrors.end_date = "End date must be after start date";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = {
      ...formData,
      budget_name: `Budget for ${formData.name}`, // Auto-generate budget name from project name
      budget_allocated: formData.budget_allocated
        ? parseFloat(formData.budget_allocated)
        : null,
      manager_id: formData.manager_id ? parseInt(formData.manager_id) : null,
      department_id: formData.department_id
        ? parseInt(formData.department_id)
        : null,
      milestones: formData.milestones.filter((m) => m.name.trim()),
      risks: formData.risks.filter((r) => r.description.trim()),
    };
    mutation.mutate(submitData);
  };

  if (isEditing && projectLoading) {
    return <div className="p-6 text-center">Loading project...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {isEditing ? "Edit Project" : "Create New Project"}
          </h1>
          <p className="text-gray-600">Fill in the project details below</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/projects")}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{errors.general}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Project Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                error={errors.name}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Project Code *
              </label>
              <Input
                value={formData.code}
                onChange={(e) =>
                  handleChange("code", e.target.value.toUpperCase())
                }
                error={errors.code}
                placeholder="e.g., PRJ-001"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Start Date *
              </label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
                error={errors.start_date}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                End Date *
              </label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
                error={errors.end_date}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Project Manager *
              </label>
              <select
                value={formData.manager_id}
                onChange={(e) => handleChange("manager_id", e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${errors.manager_id ? "border-red-500" : ""}`}
              >
                <option value="">Select Manager</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.User?.first_name || emp.first_name}{" "}
                    {emp.User?.last_name || emp.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Department
              </label>
              <select
                value={formData.department_id}
                onChange={(e) => handleChange("department_id", e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">Select Department</option>
                {departments?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Budget */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Banknote className="h-5 w-5 mr-2 text-green-600" />
            Budget
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Budget name will be auto-generated as "Budget for {formData.name || '[Project Name]'}". Finance department will assign the actual budget later.
          </p>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Total Budget
                </label>
                <Input
                  type="number"
                  value={formData.budget_allocated}
                  onChange={(e) =>
                    handleChange("budget_allocated", e.target.value)
                  }
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="NGN">NGN (₦)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Milestones & Deliverables */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-blue-600" />
              Milestones & Deliverables
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addMilestone}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Milestone
            </Button>
          </div>

          {formData.milestones.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No milestones added. Click "Add Milestone" to create one.
            </p>
          ) : (
            <div className="space-y-4">
              {formData.milestones.map((milestone, mi) => (
                <div key={milestone.id || mi} className="border rounded-lg">
                  <div
                    className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer"
                    onClick={() =>
                      setExpandedMilestones((prev) => ({
                        ...prev,
                        [milestone.id || mi]: !prev[milestone.id || mi],
                      }))
                    }
                  >
                    <div className="flex items-center gap-3">
                      {expandedMilestones[milestone.id || mi] ? (
                        <ChevronUp className="h-5 w-5" />
                      ) : (
                        <ChevronDown className="h-5 w-5" />
                      )}
                      <span className="font-medium">
                        {milestone.name || `Milestone ${mi + 1}`}
                      </span>
                      {milestone.start_date && milestone.end_date && (
                        <span className="text-sm text-gray-500">
                          ({milestone.start_date} - {milestone.end_date})
                        </span>
                      )}
                      <span className="text-sm font-medium text-primary-600">
                        Budget: {formData.currency} {(milestone.deliverables || []).reduce((sum, d) => sum + (parseFloat(d.budget) || 0), 0).toFixed(2)}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMilestone(mi);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>

                  {expandedMilestones[milestone.id || mi] && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Milestone Name *
                          </label>
                          <Input
                            value={milestone.name}
                            onChange={(e) =>
                              updateMilestone(mi, "name", e.target.value)
                            }
                            placeholder="e.g., Phase 1 - Planning"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Budget Allocation
                          </label>
                          <Input
                            type="number"
                            value={milestone.budget || ""}
                            onChange={(e) =>
                              updateMilestone(mi, "budget", e.target.value)
                            }
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Start Date
                          </label>
                          <Input
                            type="date"
                            value={milestone.start_date || ""}
                            min={formData.start_date}
                            max={formData.end_date}
                            onChange={(e) =>
                              updateMilestone(mi, "start_date", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            End Date
                          </label>
                          <Input
                            type="date"
                            value={milestone.end_date || ""}
                            min={milestone.start_date || formData.start_date}
                            max={formData.end_date}
                            onChange={(e) =>
                              updateMilestone(mi, "end_date", e.target.value)
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">
                            Status
                          </label>
                          <select
                            value={milestone.status || "pending"}
                            onChange={(e) =>
                              updateMilestone(mi, "status", e.target.value)
                            }
                            className="w-full px-3 py-2 border rounded-md"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="delayed">Delayed</option>
                          </select>
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="mt-4 pl-4 border-l-2 border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-sm text-gray-700">
                            Deliverables
                          </h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => addDeliverable(mi)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Deliverable
                          </Button>
                        </div>
                        {(milestone.deliverables || []).length === 0 ? (
                          <p className="text-sm text-gray-400">
                            No deliverables
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {milestone.deliverables.map((del, di) => {
                              const dateValid = validateDeliverableDates(
                                milestone,
                                del,
                              );
                              return (
                                <div
                                  key={di}
                                  className={`p-3 bg-gray-50 rounded ${!dateValid ? "border border-red-300" : ""}`}
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                                    <div className="md:col-span-2">
                                      <label className="block text-xs text-gray-500">
                                        Name
                                      </label>
                                      <Input
                                        value={del.name}
                                        onChange={(e) =>
                                          updateDeliverable(
                                            mi,
                                            di,
                                            "name",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Deliverable name"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500">
                                        Budget
                                      </label>
                                      <Input
                                        type="number"
                                        value={del.budget || ""}
                                        onChange={(e) =>
                                          updateDeliverable(
                                            mi,
                                            di,
                                            "budget",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500">
                                        Start
                                      </label>
                                      <Input
                                        type="date"
                                        value={del.start_date || ""}
                                        min={milestone.start_date}
                                        max={milestone.end_date}
                                        onChange={(e) =>
                                          updateDeliverable(
                                            mi,
                                            di,
                                            "start_date",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500">
                                        End
                                      </label>
                                      <Input
                                        type="date"
                                        value={del.end_date || ""}
                                        min={del.start_date || milestone.start_date}
                                        max={milestone.end_date}
                                        onChange={(e) =>
                                          updateDeliverable(
                                            mi,
                                            di,
                                            "end_date",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <select
                                        value={del.status || "pending"}
                                        onChange={(e) =>
                                          updateDeliverable(
                                            mi,
                                            di,
                                            "status",
                                            e.target.value,
                                          )
                                        }
                                        className="flex-1 px-2 py-1 border rounded text-sm"
                                      >
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">
                                          In Progress
                                        </option>
                                        <option value="completed">Done</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeDeliverable(mi, di)
                                        }
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {!dateValid && (
                                    <p className="text-xs text-red-500 mt-1">
                                      Dates must be within milestone timeline
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Risks & Issues */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
              Risks & Issues
            </h3>
            <Button type="button" variant="outline" size="sm" onClick={addRisk}>
              <Plus className="h-4 w-4 mr-1" />
              Add Risk
            </Button>
          </div>

          {formData.risks.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No risks registered
            </p>
          ) : (
            <div className="space-y-3">
              {formData.risks.map((risk, ri) => (
                <div key={ri} className="p-4 border rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-500">
                        Description
                      </label>
                      <Input
                        value={risk.description}
                        onChange={(e) =>
                          updateRisk(ri, "description", e.target.value)
                        }
                        placeholder="Describe the risk"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Impact
                      </label>
                      <select
                        value={risk.impact}
                        onChange={(e) =>
                          updateRisk(ri, "impact", e.target.value)
                        }
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Probability
                      </label>
                      <select
                        value={risk.probability}
                        onChange={(e) =>
                          updateRisk(ri, "probability", e.target.value)
                        }
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs text-gray-500">
                        Mitigation Plan
                      </label>
                      <Input
                        value={risk.mitigation || ""}
                        onChange={(e) =>
                          updateRisk(ri, "mitigation", e.target.value)
                        }
                        placeholder="How to mitigate this risk"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRisk(ri)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Client Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-purple-600" />
            Client Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Client Name
              </label>
              <Input
                value={formData.client_name}
                onChange={(e) => handleChange("client_name", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact</label>
              <Input
                value={formData.client_contact}
                onChange={(e) => handleChange("client_contact", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                type="email"
                value={formData.client_email}
                onChange={(e) => handleChange("client_email", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4">Notes</h3>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            rows={4}
            placeholder="Additional notes..."
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/projects")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={mutation.isLoading}>
            <Save className="h-4 w-4 mr-2" />
            {mutation.isLoading
              ? "Saving..."
              : isEditing
                ? "Update Project"
                : "Create Project"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
